import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ChevronLeft,
  Grid2x2,
  Home,
  List,
  Package,
  Plus,
  Search,
  ShieldCheck,
  Upload,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { InfiniteFooter } from '@/components/InfiniteFooter';
import { HScroll } from '@/components/HScroll';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { InventoryItemRow } from '@/db/types';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import {
  INVENTORY_CATEGORIES,
  getInventoryTotals,
  listInventory,
} from '@/lib/inventory';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

export default function InventoryList() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [items, setItems] = useState<InventoryItemRow[]>([]);
  const [totals, setTotals] = useState<{ count: number; totalValueCents: number; insuredCount: number }>({
    count: 0,
    totalValueCents: 0,
    insuredCount: 0,
  });
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<string | 'all'>('all');
  const [view, setView] = useState<'list' | 'grid' | 'rooms'>('grid');

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setItems(await listInventory(db, prop.id));
    setTotals(await getInventoryTotals(db, prop.id));
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filterCat !== 'all' && i.category !== filterCat) return false;
      if (!q) return true;
      return (
        i.name.toLowerCase().includes(q) ||
        (i.brand ?? '').toLowerCase().includes(q) ||
        (i.model ?? '').toLowerCase().includes(q) ||
        (i.room ?? '').toLowerCase().includes(q) ||
        (i.category ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, search, filterCat]);

  // Render only the leading slice; extend as the user scrolls toward
  // the bottom. Search filtering above scans the full `items` array so
  // results outside the current window are still surfaced when a query
  // matches them. Rooms view still aggregates from the windowed slice;
  // the totals row above already shows the full filter count so users
  // never lose sight of the "X of Y" relationship.
  const win = useInfiniteWindow(filtered, { pageSize: 25, initialSize: 20 });

  // For the "Rooms" view — bucket items by room and roll up sub-totals.
  const roomGroups = useMemo(() => {
    const map = new Map<
      string,
      { items: typeof filtered; valueCents: number; insured: number }
    >();
    for (const i of win.visible) {
      const key = i.room ?? 'Unassigned';
      if (!map.has(key)) map.set(key, { items: [], valueCents: 0, insured: 0 });
      const bucket = map.get(key)!;
      bucket.items.push(i);
      bucket.valueCents += i.value_cents ?? i.purchase_cents ?? 0;
      if (i.is_insured === 1) bucket.insured += 1;
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([room, info]) => ({ room, ...info }));
  }, [win.visible]);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Inventory</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable
            onPress={() => router.push('/inventory/import' as never)}
            style={({ pressed }) => [styles.addOutline, pressed && { opacity: 0.85 }]}
            accessibilityLabel="Import CSV">
            <Upload size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/inventory/new' as never)}
            style={({ pressed }) => [styles.add, pressed && { opacity: 0.85 }]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        {items.length > 0 ? (
          <View style={styles.statsCard}>
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>{totals.count}</Text>
              <Text style={styles.statLabel}>ITEMS</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>
                ${(totals.totalValueCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Text>
              <Text style={styles.statLabel}>TOTAL VALUE</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.statBlock}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <ShieldCheck size={16} color={Palette.forest} strokeWidth={2} />
                <Text style={styles.statValue}>{totals.insuredCount}</Text>
              </View>
              <Text style={styles.statLabel}>INSURED</Text>
            </View>
          </View>
        ) : null}

        <View style={styles.searchBox}>
          <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search inventory…"
            placeholderTextColor={Palette.inkFaint}
            style={[styles.searchInput, webNoOutline]}
          />
        </View>

        <HScroll contentStyle={styles.catRow}>
          <Pressable
            onPress={() => setFilterCat('all')}
            style={({ pressed }) => [styles.catChip, filterCat === 'all' && styles.catChipSel, pressed && { opacity: 0.9 }]}>
            <Text style={[styles.catChipText, filterCat === 'all' && styles.catChipTextSel]}>All</Text>
          </Pressable>
          {INVENTORY_CATEGORIES.map((c) => {
            const sel = filterCat === c;
            return (
              <Pressable
                key={c}
                onPress={() => setFilterCat(c)}
                style={({ pressed }) => [styles.catChip, sel && styles.catChipSel, pressed && { opacity: 0.9 }]}>
                <Text style={[styles.catChipText, sel && styles.catChipTextSel]}>{c}</Text>
              </Pressable>
            );
          })}
        </HScroll>

        <View style={styles.countRow}>
          <Text style={styles.count}>
            {filtered.length} of {items.length} items
          </Text>
          <View style={styles.viewToggle}>
            <Pressable
              onPress={() => setView('grid')}
              style={({ pressed }) => [
                styles.viewBtn,
                view === 'grid' && styles.viewBtnSel,
                pressed && { opacity: 0.8 },
              ]}>
              <Grid2x2 size={14} color={view === 'grid' ? Palette.cream : Palette.ink} strokeWidth={1.8} />
            </Pressable>
            <Pressable
              onPress={() => setView('list')}
              style={({ pressed }) => [
                styles.viewBtn,
                view === 'list' && styles.viewBtnSel,
                pressed && { opacity: 0.8 },
              ]}>
              <List size={14} color={view === 'list' ? Palette.cream : Palette.ink} strokeWidth={1.8} />
            </Pressable>
            <Pressable
              onPress={() => setView('rooms')}
              style={({ pressed }) => [
                styles.viewBtn,
                view === 'rooms' && styles.viewBtnSel,
                pressed && { opacity: 0.8 },
              ]}>
              <Home size={14} color={view === 'rooms' ? Palette.cream : Palette.ink} strokeWidth={1.8} />
            </Pressable>
          </View>
        </View>

        {filtered.length === 0 ? (
          <View style={styles.empty}>
            <Package size={42} color={Palette.inkFaint} strokeWidth={1.4} />
            <Text style={styles.emptyTitle}>
              {items.length === 0 ? 'Nothing in your inventory yet' : 'No matches'}
            </Text>
            <Text style={styles.emptyBody}>
              {items.length === 0
                ? 'Add anything worth tracking for insurance, resale, or moving — electronics, appliances, furniture, jewelry, tools.'
                : 'Try a different filter or clear the search.'}
            </Text>
            {items.length === 0 ? (
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
                <Pressable
                  onPress={() => router.push('/inventory/new' as never)}
                  style={({ pressed }) => [styles.emptyCta, pressed && { transform: [{ scale: 0.98 }] }]}>
                  <Plus size={16} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.emptyCtaText}>Add an item</Text>
                </Pressable>
                <Pressable
                  onPress={() => router.push('/inventory/import' as never)}
                  style={({ pressed }) => [styles.emptyCtaSecondary, pressed && { transform: [{ scale: 0.98 }] }]}>
                  <Text style={styles.emptyCtaSecondaryText}>Import CSV</Text>
                </Pressable>
              </View>
            ) : null}
          </View>
        ) : view === 'rooms' ? (
          <View style={{ paddingHorizontal: Spacing.four, marginTop: 8, gap: 12 }}>
            {roomGroups.map((g) => (
              <View key={g.room} style={styles.roomCard}>
                <View style={styles.roomHead}>
                  <View style={styles.roomIcon}>
                    <Home size={16} color={Palette.forest} strokeWidth={1.8} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.roomTitle}>{g.room}</Text>
                    <Text style={styles.roomMeta}>
                      {g.items.length} item{g.items.length === 1 ? '' : 's'}
                      {g.valueCents > 0
                        ? ` · $${(g.valueCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : ''}
                      {g.insured > 0 ? ` · ${g.insured} insured` : ''}
                    </Text>
                  </View>
                </View>
                <View style={styles.roomItems}>
                  {g.items.map((item) => (
                    <Pressable
                      key={item.id}
                      onPress={() => router.push(`/inventory/${item.id}` as never)}
                      style={({ pressed }) => [
                        styles.roomItemRow,
                        pressed && { backgroundColor: '#EEF3EE' },
                      ]}>
                      {item.photo_uri ? (
                        <Image source={{ uri: item.photo_uri }} style={styles.roomItemThumb} />
                      ) : (
                        <View style={[styles.roomItemThumb, styles.rowPlaceholder]}>
                          <Package size={16} color={Palette.forest} strokeWidth={1.6} />
                        </View>
                      )}
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.roomItemName} numberOfLines={1}>{item.name}</Text>
                        <Text style={styles.roomItemMeta} numberOfLines={1}>
                          {[item.brand, item.category].filter(Boolean).join(' · ')}
                        </Text>
                      </View>
                      {(item.value_cents ?? item.purchase_cents) ? (
                        <Text style={styles.roomItemValue}>
                          ${(((item.value_cents ?? item.purchase_cents) ?? 0) / 100).toLocaleString()}
                        </Text>
                      ) : null}
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}
          </View>
        ) : view === 'grid' ? (
          <View style={styles.grid}>
            {win.visible.map((item) => (
              <Pressable
                key={item.id}
                onPress={() => router.push(`/inventory/${item.id}` as never)}
                style={({ pressed }) => [styles.gridCell, pressed && { transform: [{ scale: 0.97 }] }]}>
                <View style={styles.gridThumbWrap}>
                  {item.photo_uri ? (
                    <Image source={{ uri: item.photo_uri }} style={styles.gridThumb} />
                  ) : (
                    <View style={[styles.gridThumb, styles.gridPlaceholder]}>
                      <Package size={28} color={Palette.forest} strokeWidth={1.5} />
                    </View>
                  )}
                  {item.is_insured === 1 ? (
                    <View style={styles.insuredBadge}>
                      <ShieldCheck size={12} color={Palette.cream} strokeWidth={2} />
                    </View>
                  ) : null}
                </View>
                <Text style={styles.gridTitle} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.gridSub} numberOfLines={1}>
                  {[item.brand, item.room].filter(Boolean).join(' · ') || item.category || 'Untagged'}
                </Text>
                {(item.value_cents ?? item.purchase_cents) ? (
                  <Text style={styles.gridValue}>
                    ${(((item.value_cents ?? item.purchase_cents) ?? 0) / 100).toLocaleString()}
                  </Text>
                ) : null}
              </Pressable>
            ))}
          </View>
        ) : (
          <View style={styles.list}>
            {win.visible.map((item) => (
              <Pressable
                key={item.id}
                onPress={() => router.push(`/inventory/${item.id}` as never)}
                style={({ pressed }) => [styles.row, pressed && { transform: [{ scale: 0.99 }] }]}>
                {item.photo_uri ? (
                  <Image source={{ uri: item.photo_uri }} style={styles.rowThumb} />
                ) : (
                  <View style={[styles.rowThumb, styles.rowPlaceholder]}>
                    <Package size={22} color={Palette.forest} strokeWidth={1.6} />
                  </View>
                )}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>{item.name}</Text>
                  <Text style={styles.rowSub} numberOfLines={1}>
                    {[item.brand, item.category, item.room].filter(Boolean).join(' · ')}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  {(item.value_cents ?? item.purchase_cents) ? (
                    <Text style={styles.rowValue}>
                      ${(((item.value_cents ?? item.purchase_cents) ?? 0) / 100).toLocaleString()}
                    </Text>
                  ) : null}
                  {item.is_insured === 1 ? (
                    <View style={styles.rowInsured}>
                      <ShieldCheck size={11} color={Palette.forest} strokeWidth={2} />
                      <Text style={styles.rowInsuredText}>Insured</Text>
                    </View>
                  ) : null}
                </View>
              </Pressable>
            ))}
          </View>
        )}
        {filtered.length > 0 ? (
          <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="item" />
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  add: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  addOutline: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  statsCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
  },
  statBlock: { flex: 1, alignItems: 'center', paddingHorizontal: 6 },
  divider: { width: 1, height: 32, backgroundColor: Palette.line },
  statValue: { fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.forest, letterSpacing: -0.4 },
  statLabel: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.inkFaint,
    letterSpacing: 0.5, marginTop: 2,
  },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderWidth: 1.5,
    borderColor: Palette.line, borderRadius: 11,
    paddingHorizontal: 11,
  },
  searchInput: {
    flex: 1, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.ink,
  },
  catRow: {
    paddingHorizontal: Spacing.four,
    marginTop: 10,
    gap: 6,
  },
  catChip: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
    marginRight: 6,
  },
  catChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  catChipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  catChipTextSel: { color: Palette.cream },
  countRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: Spacing.four,
    marginTop: 12,
  },
  count: { fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.inkFaint },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 8, overflow: 'hidden',
  },
  viewBtn: {
    width: 32, height: 28,
    alignItems: 'center', justifyContent: 'center',
  },
  viewBtnSel: { backgroundColor: Palette.forest },
  empty: {
    alignItems: 'center', paddingHorizontal: 32, paddingVertical: 56, gap: 12,
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  emptyBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 19,
  },
  emptyCta: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: Palette.forest,
    paddingHorizontal: 16, paddingVertical: 11, borderRadius: 14,
  },
  emptyCtaText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  emptyCtaSecondary: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    paddingHorizontal: 16, paddingVertical: 11, borderRadius: 14,
  },
  emptyCtaSecondaryText: { color: Palette.forest, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  grid: {
    flexDirection: 'row', flexWrap: 'wrap',
    paddingHorizontal: 14, marginTop: 8, gap: 8,
  },
  gridCell: {
    width: '47%', flexGrow: 1,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 14, padding: 8, gap: 4,
  },
  gridThumbWrap: { position: 'relative' },
  gridThumb: {
    width: '100%', aspectRatio: 4 / 3, borderRadius: 10,
  },
  gridPlaceholder: {
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  insuredBadge: {
    position: 'absolute', top: 6, right: 6,
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  gridTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink, paddingHorizontal: 4, marginTop: 2 },
  gridSub: { fontFamily: Fonts.sansMedium, fontSize: 11.5, color: Palette.inkSoft, paddingHorizontal: 4 },
  gridValue: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.honeyDeep, paddingHorizontal: 4, marginTop: 2 },
  list: { paddingHorizontal: 18, marginTop: 8, gap: 8 },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: 14, padding: 11,
  },
  rowThumb: { width: 50, height: 50, borderRadius: 12 },
  rowPlaceholder: {
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  rowTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  rowSub: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    marginTop: 2,
  },
  rowValue: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.honeyDeep },
  rowInsured: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 2,
  },
  rowInsuredText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.forest,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  roomCard: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    overflow: 'hidden',
  },
  roomHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    backgroundColor: '#FAF3E2',
  },
  roomIcon: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  roomTitle: { fontFamily: Fonts.serifBold, fontSize: 15, color: Palette.ink },
  roomMeta: {
    fontFamily: Fonts.sansSemibold, fontSize: 12.5, color: Palette.inkSoft,
    marginTop: 1,
  },
  roomItems: { padding: 8, gap: 2 },
  roomItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 10,
  },
  roomItemThumb: {
    width: 38, height: 38, borderRadius: 10,
  },
  roomItemName: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  roomItemMeta: {
    fontFamily: Fonts.sansMedium, fontSize: 12, color: Palette.inkSoft, marginTop: 1,
  },
  roomItemValue: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.honeyDeep,
  },
});
